import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Mail, Send, Play, TrendingUp, Eye, Code as CodeIcon, Monitor } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMAIL_META = {
  a: { name: 'Bienvenue',        trigger: 'Inscription (J+0)' },
  a2:{ name: 'Bienvenue Fondateur', trigger: 'Inscription via CTA « Devenir Fondateur »' },
  b: { name: 'IA fonctionnement', trigger: 'J+1 automatique' },
  c: { name: 'Analyses attendent',trigger: 'J+2 si 0 analyse' },
  d: { name: 'Packs à la carte',  trigger: 'J+3 automatique' },
  e: { name: 'Money Management',  trigger: 'J+5 automatique' },
  f: { name: 'Scan Auto',         trigger: 'J+7 automatique' },
  g: { name: 'Dernière relance',  trigger: 'J+3 si 0 analyse' },
  h: { name: 'Analyses expirent', trigger: 'J+4 si 0 analyse' },
  i: { name: 'Top 10% habitudes', trigger: 'J+10 automatique' },
  k: { name: 'Limite atteinte',   trigger: 'Événementiel · quota' },
  l: { name: 'Promo -20% Pro',    trigger: 'J+14 automatique' },
  m: { name: 'ROI Expert',        trigger: 'J+21 automatique' },
  n: { name: 'Fondateur rareté',  trigger: 'Manuel · campagne' },
  r: { name: 'Win-back',          trigger: 'J+3 post-annulation' },
  p1: { name: 'Paiement confirmé', trigger: 'Webhook Stripe · checkout' },
  p2: { name: 'Renouvellement',    trigger: 'Webhook Stripe · invoice paid' },
  p3: { name: 'Paiement échoué',   trigger: 'Webhook Stripe · invoice failed' },
  pwc:{ name: 'Mot de passe modifié', trigger: 'Événementiel · sécurité' },
};

export default function AdminEmails() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningScheduler, setRunningScheduler] = useState(false);

  // Preview modal state
  const [previewCode, setPreviewCode] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState('render'); // 'render' | 'html'
  const [testSending, setTestSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/emails/stats`);
      setStats(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runScheduler = async () => {
    setRunningScheduler(true);
    try {
      const { data } = await axios.post(`${API}/admin/emails/scheduler/run`);
      toast.success(`Scheduler exécuté — ${data.scanned} users scannés, ${data.sent} emails envoyés`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur scheduler');
    } finally {
      setRunningScheduler(false);
    }
  };

  const sendFounderCampaign = async () => {
    if (!window.confirm('Envoyer la campagne Fondateur à tous les users Free ? Cette action envoie un email à chaque user Free actif.')) return;
    try {
      const { data } = await axios.post(`${API}/admin/emails/campaign/founder`, { places_left: 30, tier: 'free' });
      toast.success(`Campagne Fondateur : ${data.sent} emails envoyés`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur campagne');
    }
  };

  const openPreview = async (code) => {
    setPreviewCode(code);
    setPreviewData(null);
    setPreviewMode('render');
    setPreviewLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/emails/preview/${code}`);
      setPreviewData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur aperçu');
      setPreviewCode(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewCode(null);
    setPreviewData(null);
  };

  const sendTestToMe = async () => {
    if (!previewCode) return;
    setTestSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/emails/test-send/${previewCode}`);
      toast.success(`Email test envoyé à ${data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi test');
    } finally {
      setTestSending(false);
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;

  const byCode = {};
  (stats?.by_email || []).forEach(s => { byCode[s.code] = s; });
  const allCodes = Object.keys(EMAIL_META);

  return (
    <div className="space-y-4" data-testid="admin-emails-panel">
      {/* Header + actions */}
      <Card className="p-4 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-6 h-6 text-emerald-400" />
          <div>
            <div className="text-lg font-bold">Emails automatiques</div>
            <div className="text-xs text-muted-foreground">
              {stats?.total_sent || 0} email(s) envoyé(s) au total · Provider Resend
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runScheduler} disabled={runningScheduler} data-testid="run-scheduler-btn">
            {runningScheduler ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Lancer scheduler
          </Button>
          <Button size="sm" onClick={sendFounderCampaign} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="send-founder-btn">
            <Send className="w-4 h-4 mr-1" />
            Campagne Fondateur
          </Button>
        </div>
      </Card>

      {/* Table des emails */}
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 border-b border-white/10 text-muted-foreground">
            <tr>
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Déclencheur</th>
              <th className="text-right p-3">Envoyés</th>
              <th className="text-right p-3">Échoués</th>
              <th className="text-right p-3">Ouvertures</th>
              <th className="text-right p-3">Clics</th>
              <th className="text-left p-3">Dernier envoi</th>
              <th className="text-center p-3">Aperçu</th>
            </tr>
          </thead>
          <tbody>
            {allCodes.map(code => {
              const s = byCode[code] || { sent: 0, failed: 0, opens: 0, clicks: 0, open_rate_pct: 0, click_rate_pct: 0, last_sent: null };
              return (
                <tr key={code} className="border-b border-white/5" data-testid={`email-row-${code}`}>
                  <td className="p-3 font-mono font-bold text-emerald-400">{code.toUpperCase()}</td>
                  <td className="p-3">{EMAIL_META[code].name}</td>
                  <td className="p-3 text-xs text-muted-foreground">{EMAIL_META[code].trigger}</td>
                  <td className="p-3 text-right font-mono">{s.sent}</td>
                  <td className="p-3 text-right font-mono">{s.failed > 0 ? <span className="text-red-400">{s.failed}</span> : 0}</td>
                  <td className="p-3 text-right font-mono">{s.opens} {s.sent > 0 && <span className="text-xs text-muted-foreground">({s.open_rate_pct}%)</span>}</td>
                  <td className="p-3 text-right font-mono">{s.clicks} {s.sent > 0 && <span className="text-xs text-muted-foreground">({s.click_rate_pct}%)</span>}</td>
                  <td className="p-3 text-xs text-muted-foreground">{s.last_sent ? s.last_sent.substring(0, 16).replace('T', ' ') : '—'}</td>
                  <td className="p-3 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2"
                      onClick={() => openPreview(code)}
                      data-testid={`preview-email-btn-${code}`}
                      title="Voir le contenu de l'email"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Info footer */}
      <Card className="p-4 border-amber-500/20 bg-amber-500/5 text-sm">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-300 mb-1">Notes techniques</div>
            <ul className="text-muted-foreground text-xs space-y-1 list-disc list-inside">
              <li>Les ouvertures et clics sont trackés via Resend (dashboard resend.com pour le détail temps réel)</li>
              <li>En mode test Resend, seuls les emails envoyés à jeremy.vermot@gmail.com aboutissent. Une fois <code>tradex-ai.fr</code> vérifié, tous les users recevront leurs emails.</li>
              <li>Le scheduler est exécutable manuellement ici. Pour l&apos;automatisation horaire, configurez un cron externe qui appelle <code>POST /api/admin/emails/scheduler/run</code>.</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Preview modal */}
      <Dialog open={!!previewCode} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent
          className="max-w-4xl w-[95vw] p-0 gap-0 max-h-[90vh] flex flex-col"
          data-testid="email-preview-modal"
        >
          <DialogHeader className="p-4 pb-3 border-b border-white/10">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Mail className="w-4 h-4 text-emerald-400" />
              Aperçu — {previewCode && EMAIL_META[previewCode]?.name}
              <span className="font-mono text-xs text-emerald-400/70 ml-1">
                [{previewCode?.toUpperCase()}]
              </span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              {previewData?.subject
                ? <span><strong>Objet :</strong> {previewData.subject}</span>
                : 'Chargement du contenu…'}
            </DialogDescription>
          </DialogHeader>

          {/* Toolbar */}
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-white/10 bg-white/[0.02]">
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={previewMode === 'render' ? 'default' : 'outline'}
                className="h-7 px-2 text-xs"
                onClick={() => setPreviewMode('render')}
                data-testid="preview-mode-render-btn"
              >
                <Monitor className="w-3.5 h-3.5 mr-1" />
                Rendu
              </Button>
              <Button
                size="sm"
                variant={previewMode === 'html' ? 'default' : 'outline'}
                className="h-7 px-2 text-xs"
                onClick={() => setPreviewMode('html')}
                data-testid="preview-mode-html-btn"
              >
                <CodeIcon className="w-3.5 h-3.5 mr-1" />
                HTML brut
              </Button>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={sendTestToMe}
              disabled={testSending || !previewData}
              data-testid="preview-send-test-btn"
            >
              {testSending
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Send className="w-3.5 h-3.5 mr-1" />}
              M&apos;envoyer un test
            </Button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden bg-white/[0.02]">
            {previewLoading && (
              <div className="h-full flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
              </div>
            )}
            {!previewLoading && previewData && previewMode === 'render' && (
              <iframe
                title="email-preview"
                srcDoc={previewData.html}
                className="w-full h-[70vh] bg-white"
                sandbox=""
                data-testid="preview-iframe"
              />
            )}
            {!previewLoading && previewData && previewMode === 'html' && (
              <pre
                className="w-full h-[70vh] overflow-auto p-4 text-xs font-mono text-emerald-100/90 bg-black/40 whitespace-pre-wrap break-all"
                data-testid="preview-html-raw"
              >
                {previewData.html}
              </pre>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
